import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '../api'
import { useUserStore } from './userStore'
import { useProfileStore } from './profileStore'
import { useSongStore } from './songStore'
import { usePresenceStore } from './presenceStore'
import { useUiStore } from './uiStore'
import type { MixDetailsDto, MixTrackDto, CreateMixTrackDto, MixDto, AudioClipDto, UpdateMixDto, UpdateMixTrackDto, UpdateAudioClipDto, SplitAudioClipDto, RenderDrumsCompareResultDto } from '../dtos'
import { audioEngine } from '../audio/AudioEngine'
import { pollJob } from '../utils/pollJob'

export const STATIC_BASE_URL = import.meta.env.VITE_STATIC_BASE_URL || ''
export const pixelsPerSecond = ref(50)

const ZOOM_STEPS = [10, 15, 25, 35, 50, 75, 100, 150, 200, 300, 400, 500]
const DEFAULT_ZOOM_INDEX = 4 // 50
const zoomIndex = ref(DEFAULT_ZOOM_INDEX)

// MPEG bitrate lookup tables [version][layer][index]
const BITRATES: Record<string, number[]> = {
  'V1L3': [0,32,40,48,56,64,80,96,112,128,160,192,224,256,320,0],
  'V1L2': [0,32,48,56,64,80,96,112,128,160,192,224,256,320,384,0],
  'V2L3': [0,8,16,24,32,40,48,56,64,80,96,112,128,144,160,0],
}
const SAMPLE_RATES: Record<number, number[]> = {
  3: [44100, 48000, 32000], // MPEG1
  2: [22050, 24000, 16000], // MPEG2
  0: [11025, 12000, 8000],  // MPEG2.5
}
const SAMPLES_PER_FRAME: Record<number, number> = { 3: 1152, 2: 1152, 0: 576 }

/**
 * Parses MP3 frame headers to count frames and compute exact duration.
 * Works correctly for VBR files even without Xing headers.
 */
function getMp3Duration(data: Uint8Array): number | null {
  let offset = 0
  // Skip ID3v2 tag if present
  if (data[0] === 0x49 && data[1] === 0x44 && data[2] === 0x33) {
    const size = ((data[6]! & 0x7f) << 21) | ((data[7]! & 0x7f) << 14) |
                 ((data[8]! & 0x7f) << 7) | (data[9]! & 0x7f)
    offset = 10 + size
  }

  let frames = 0
  let sampleRate = 0
  let mpegVersion = 0

  while (offset < data.length - 4) {
    // Look for frame sync: 11 set bits
    if (data[offset] === 0xff && ((data[offset + 1]!) & 0xe0) === 0xe0) {
      const b1 = data[offset + 1]!
      const b2 = data[offset + 2]!
      const version = (b1 >> 3) & 3    // 0=2.5, 2=2, 3=1
      const layer = (b1 >> 1) & 3      // 1=L3, 2=L2, 3=L1
      const brIndex = (b2 >> 4) & 0xf
      const srIndex = (b2 >> 2) & 3
      const padding = (b2 >> 1) & 1

      if (version === 1 || layer === 0 || brIndex === 0 || brIndex === 15 || srIndex === 3) {
        offset++
        continue
      }

      const vKey = version === 3 ? 'V1' : 'V2'
      const lKey = layer === 1 ? 'L3' : 'L2'
      const bitrate = BITRATES[`${vKey}${lKey}`]?.[brIndex]
      const sr = SAMPLE_RATES[version]?.[srIndex]

      if (!bitrate || !sr) { offset++; continue }

      if (frames === 0) { sampleRate = sr; mpegVersion = version }

      const frameSize = layer === 1
        ? Math.floor(144000 * bitrate / sr) + padding
        : Math.floor(72000 * bitrate / sr) + padding

      frames++
      offset += frameSize
    } else {
      offset++
    }
  }

  if (frames === 0 || sampleRate === 0) return null
  const samplesPerFrame = SAMPLES_PER_FRAME[mpegVersion] ?? 1152
  return (frames * samplesPerFrame) / sampleRate
}

export const useMixStore = defineStore('mix', () => {
  const userStore = useUserStore()
  const presenceStore = usePresenceStore()
  const uiStore = useUiStore()
  const currentMix = ref<MixDetailsDto | null>(null)
  const loading = ref(false)
  const uploading = ref(false)
  const isPlaying = ref(false)
  const isRecording = ref(false)
  const armedTrackId = ref<number | null>(null)
  const currentTime = ref(0)
  const masterLevel = ref(0)
  const recordStartTime = ref(0)
  const metronomeEnabled = ref(false)
  const metronomeBpm = ref(120)
  const metronomeBeatsPerBar = ref(4)

  // Transient cross-track drag preview state
  const dragState = ref<{
    clipId: number
    sourceTrackId: number
    destTrackId: number
    timelineStart: number
    duration: number
  } | null>(null)

  let timer: number | null = null

  async function fetchMixDetails(mixId: number) {
    if (!userStore.user) return
    loading.value = true
    try {
      const token = await userStore.user.getIdToken()
      const response = await api.get(`/mixes/${mixId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      currentMix.value = response.data
      
      // START PRESENCE
      presenceStore.startPresence('Mix', mixId)

      // Preload audio files
      if (currentMix.value) {
        await audioEngine.preloadMix(currentMix.value, STATIC_BASE_URL)
      }
    } catch (error) {
      console.error('Failed to fetch mix details:', error)
    } finally {
      loading.value = false
    }
  }

  async function addTrack(name: string) {
    if (!userStore.user || !currentMix.value) return
    try {
      const token = await userStore.user.getIdToken()
      const payload: CreateMixTrackDto = {
        name,
        mixId: currentMix.value.id,
        order: currentMix.value.tracks.length
      }
      const response = await api.post('/mixes/tracks', payload, {
        headers: { Authorization: `Bearer ${token}` }
      })
      currentMix.value.tracks.push({ ...response.data, clips: [] })
      return response.data
    } catch (error) {
      console.error('Failed to add track:', error)
      throw error
    }
  }

  async function updateTrack(trackId: number, data: Partial<MixTrackDto>) {
    if (!userStore.user || !currentMix.value) return
    try {
      const track = currentMix.value.tracks.find(t => t.id === trackId)
      if (!track) return

      const payload: UpdateMixTrackDto = {
        ...data,
        rowVersion: track.rowVersion
      }
      
      const token = await userStore.user.getIdToken()
      const response = await api.put(`/mixes/tracks/${trackId}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      })

      // Update locally (including NEW RowVersion) — preserve clips since the API doesn't include them on track updates
      const { clips, ...trackData } = response.data
      Object.assign(track, trackData)

      // Update audio engine in real-time
      if (data.isSolo !== undefined) {
        audioEngine.applySoloState(currentMix.value.tracks)
      } else if (data.volume !== undefined || data.isMuted !== undefined) {
        audioEngine.applySoloState(currentMix.value.tracks)
      }
      if (data.pan !== undefined) {
        audioEngine.updateTrackPan(trackId, track.pan)
      }
    } catch (error: any) {
      if (error.response?.status === 409 && currentMix.value) {
        error._handled = true
        const { useUiStore } = await import('./uiStore')
        const uiStore = useUiStore()
        const mixId = currentMix.value.id
        uiStore.triggerConcurrencyError(
          error.response.data || 'This track was modified by another user.',
          true,
          async () => { await fetchMixDetails(mixId) }
        )
        return
      }
      console.error('Failed to update track:', error)
      throw error
    }
  }

  async function deleteTrack(trackId: number) {
    if (!userStore.user || !currentMix.value) return
    try {
      const token = await userStore.user.getIdToken()
      await api.delete(`/mixes/tracks/${trackId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      currentMix.value.tracks = currentMix.value.tracks.filter(t => t.id !== trackId)
    } catch (error) {
      console.error('Failed to delete track:', error)
    }
  }

  async function addClip(trackId: number, file: File, timelineStart: number, knownDuration?: number, aiOpts?: { prompt: string, geminiResponse: string, instrument: string, inputTokens?: number, outputTokens?: number, aiModel?: string, aiThinking?: boolean }) {
    if (!userStore.user || !currentMix.value) return
    uploading.value = true
    try {
      const token = await userStore.user.getIdToken()
      const formData = new FormData()
      formData.append('file', file)
      formData.append('timelineStart', timelineStart.toString())
      formData.append('sourceOffset', '0')
      if (aiOpts) {
        formData.append('isAiGenerated', 'true')
        formData.append('aiInstrument', aiOpts.instrument)
        formData.append('aiPrompt', aiOpts.prompt)
        formData.append('aiResponse', aiOpts.geminiResponse)
        if (aiOpts.inputTokens !== undefined) formData.append('aiInputTokens', aiOpts.inputTokens.toString())
        if (aiOpts.outputTokens !== undefined) formData.append('aiOutputTokens', aiOpts.outputTokens.toString())
        if (aiOpts.aiModel) formData.append('aiModel', aiOpts.aiModel)
        if (aiOpts.aiThinking !== undefined) formData.append('aiThinking', aiOpts.aiThinking.toString())
      }

      let duration = knownDuration
      if (duration === undefined) {
        // For MP3s, parse frame headers directly to get exact duration.
        // Browser APIs (Audio.duration, AudioBuffer.duration) can wildly overestimate
        // duration for VBR MP3s with missing/incorrect Xing headers.
        if (file.type === 'audio/mpeg' || file.name.toLowerCase().endsWith('.mp3')) {
          const arrayBuffer = await file.arrayBuffer()
          duration = getMp3Duration(new Uint8Array(arrayBuffer)) ?? undefined
        }
        if (duration === undefined || duration === null) {
          const tempUrl = URL.createObjectURL(file)
          const audio = new Audio(tempUrl)
          await new Promise(resolve => audio.onloadedmetadata = resolve)
          duration = audio.duration
          URL.revokeObjectURL(tempUrl)
        }
      }
      formData.append('duration', duration.toString())

      const response = await api.post(`/mixes/tracks/${trackId}/clips`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      })

      const track = currentMix.value.tracks.find(t => t.id === trackId)
      if (track) {
        track.clips.push(response.data)
        await audioEngine.loadClip(response.data, STATIC_BASE_URL)
      }
      return response.data
    } catch (error) {
      console.error('Failed to add clip:', error)
      throw error
    } finally {
      uploading.value = false
    }
  }

  async function deleteClip(clipId: number) {
    if (!userStore.user || !currentMix.value) return
    try {
      const token = await userStore.user.getIdToken()
      await api.delete(`/mixes/clips/${clipId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      for (const track of currentMix.value.tracks) {
        track.clips = track.clips.filter(c => c.id !== clipId)
      }
    } catch (error) {
      console.error('Failed to delete clip:', error)
    }
  }

  async function moveClipToTrack(clipId: number, destTrackId: number, timelineStart?: number) {
    if (!userStore.user || !currentMix.value) return
    let sourceTrack: MixTrackDto | undefined
    let clip: AudioClipDto | undefined
    let clipIndex = -1
    for (const track of currentMix.value.tracks) {
      const idx = track.clips.findIndex(c => c.id === clipId)
      if (idx !== -1) { sourceTrack = track; clip = track.clips[idx]; clipIndex = idx; break }
    }
    if (!sourceTrack || !clip) return
    if (sourceTrack.id === destTrackId) {
      if (timelineStart !== undefined) await updateClip(clipId, { timelineStart })
      return
    }
    const destTrack = currentMix.value.tracks.find(t => t.id === destTrackId)
    if (!destTrack) return

    const originalTrackId = clip.mixTrackId
    const originalStart = clip.timelineStart
    sourceTrack.clips.splice(clipIndex, 1)
    clip.mixTrackId = destTrackId
    if (timelineStart !== undefined) clip.timelineStart = timelineStart
    destTrack.clips.push(clip)

    try {
      const token = await userStore.user.getIdToken()
      const payload: UpdateAudioClipDto = {
        mixTrackId: destTrackId,
        rowVersion: clip.rowVersion,
        ...(timelineStart !== undefined ? { timelineStart } : {})
      }
      const response = await api.put(`/mixes/clips/${clipId}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      })
      Object.assign(clip, response.data)
    } catch (error: any) {
      // Rollback on failure
      destTrack.clips.splice(destTrack.clips.indexOf(clip), 1)
      clip.mixTrackId = originalTrackId
      clip.timelineStart = originalStart
      sourceTrack.clips.splice(clipIndex, 0, clip)
      if (error.response?.status === 409 && currentMix.value) {
        error._handled = true
        const mixId = currentMix.value.id
        uiStore.triggerConcurrencyError(
          error.response.data || 'This clip was modified by another user.',
          true,
          async () => { await fetchMixDetails(mixId) }
        )
        return
      }
      throw error
    }
  }

  async function updateClip(clipId: number, data: Partial<AudioClipDto>) {
    if (!userStore.user || !currentMix.value) return
    try {
      let clip: AudioClipDto | undefined
      for (const track of currentMix.value.tracks) {
        clip = track.clips.find(c => c.id === clipId)
        if (clip) break
      }
      if (!clip) return

      const payload: UpdateAudioClipDto = {
        ...data,
        rowVersion: clip.rowVersion
      }

      const token = await userStore.user.getIdToken()
      const response = await api.put(`/mixes/clips/${clipId}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      // Update locally
      Object.assign(clip, response.data)
    } catch (error: any) {
      if (error.response?.status === 409 && currentMix.value) {
        error._handled = true
        const { useUiStore } = await import('./uiStore')
        const uiStore = useUiStore()
        const mixId = currentMix.value.id
        uiStore.triggerConcurrencyError(
          error.response.data || 'This clip was modified by another user.',
          true,
          async () => { await fetchMixDetails(mixId) }
        )
        return
      }
      console.error('Failed to update clip:', error)
      throw error
    }
  }

  async function splitClip(clipId: number, splitAtTime: number) {
    if (!userStore.user || !currentMix.value) return
    try {
      let clip: AudioClipDto | undefined
      let track: MixTrackDto | undefined
      for (const t of currentMix.value.tracks) {
        clip = t.clips.find(c => c.id === clipId)
        if (clip) { track = t; break }
      }
      if (!clip || !track) return

      const payload: SplitAudioClipDto = {
        splitAtTime,
        rowVersion: clip.rowVersion
      }

      const token = await userStore.user.getIdToken()
      const response = await api.post(`/mixes/clips/${clipId}/split`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      })

      const { left, right } = response.data as { left: AudioClipDto; right: AudioClipDto }

      // Replace the original clip with left, add right
      const idx = track.clips.findIndex(c => c.id === clipId)
      if (idx !== -1) {
        track.clips.splice(idx, 1, left)
      }
      track.clips.push(right)

      // Preload the right clip (left reuses same buffer range)
      await audioEngine.loadClip(right, STATIC_BASE_URL)
    } catch (error: any) {
      if (error.response?.status === 409 && currentMix.value) {
        error._handled = true
        const { useUiStore } = await import('./uiStore')
        const uiStore = useUiStore()
        const mixId = currentMix.value.id
        uiStore.triggerConcurrencyError(
          error.response.data || 'This clip was modified by another user.',
          true,
          async () => { await fetchMixDetails(mixId) }
        )
        return
      }
      console.error('Failed to split clip:', error)
      throw error
    }
  }

  async function updateMix(data: Partial<MixDto>) {
    if (!userStore.user || !currentMix.value) return
    try {
      const token = await userStore.user.getIdToken()
      const payload: UpdateMixDto = {
        ...data,
        rowVersion: currentMix.value.rowVersion
      }
      const response = await api.put(`/mixes/${currentMix.value.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      // Update locally
      Object.assign(currentMix.value, response.data)
    } catch (error: any) {
      if (error.response?.status === 409 && currentMix.value) {
        error._handled = true
        const { useUiStore } = await import('./uiStore')
        const uiStore = useUiStore()
        const mixId = currentMix.value.id
        uiStore.triggerConcurrencyError(
          error.response.data || 'This mix was modified by another user.',
          true,
          async () => { await fetchMixDetails(mixId) }
        )
        return
      }
      console.error('Failed to update mix:', error)
      throw error
    }
  }

  function play() {
    if (!currentMix.value) return
    audioEngine.play(currentMix.value, currentTime.value, STATIC_BASE_URL)
    if (metronomeEnabled.value) {
      audioEngine.startMetronome(metronomeBpm.value, metronomeBeatsPerBar.value, currentTime.value)
    }
    isPlaying.value = true

    // Start progress timer
    if (timer) clearInterval(timer)
    timer = window.setInterval(() => {
      currentTime.value = audioEngine.currentPlaybackTime
      masterLevel.value = audioEngine.getMasterLevel()

      // Stop playback when reaching the mix duration
      const durationSeconds = currentMix.value?.durationSeconds ?? 0
      if (durationSeconds > 0 && currentTime.value >= durationSeconds) {
        currentTime.value = durationSeconds
        stop()
      }
    }, 50)
  }

  function stop() {
    audioEngine.stop()
    isPlaying.value = false
    masterLevel.value = 0
    if (timer) {
      clearInterval(timer)
      timer = null
    }
  }

  async function stopRecording() {
    if (!isRecording.value || armedTrackId.value === null) return
    const trackId = armedTrackId.value
    const startTime = recordStartTime.value
    const recordedDuration = currentTime.value - startTime

    // Stop playback and recording together
    stop()
    const file = await audioEngine.stopRecordingAsync()
    isRecording.value = false

    if (file && currentMix.value && recordedDuration > 0) {
      await addClip(trackId, file, startTime, recordedDuration)
    }
  }

  async function toggleRecord() {
    if (isRecording.value) {
      await stopRecording()
      return
    }

    if (armedTrackId.value === null || !currentMix.value) return

    // Always request access with the latest profile settings — engine no-ops if
    // the device + channel match what's already active, and re-acquires otherwise.
    const profileStore = useProfileStore()
    const channel = (profileStore.profile?.audioInputChannel ?? 'stereo') as 'stereo' | 'left' | 'right'
    const granted = await audioEngine.requestMicAccess({
      deviceId: profileStore.profile?.audioInputDeviceId ?? null,
      channel,
    })
    if (!granted) return

    // Start recording + playback simultaneously
    recordStartTime.value = currentTime.value
    const started = audioEngine.startRecording()
    if (!started) return

    isRecording.value = true
    play()
  }

  function armTrack(trackId: number) {
    // Toggle: if already armed, disarm; otherwise arm the new track
    armedTrackId.value = armedTrackId.value === trackId ? null : trackId
  }

  function seek(time: number) {
    // Don't allow seeking while recording
    if (isRecording.value) return
    const wasPlaying = isPlaying.value
    if (wasPlaying) stop()
    currentTime.value = Math.max(0, time)
    masterLevel.value = 0
    if (wasPlaying) play()
  }

  function clear() {
    stop()
    isRecording.value = false
    armedTrackId.value = null
    currentMix.value = null
    presenceStore.stopPresence()
  }

  const generating = ref(false)
  const generateError = ref('')

  async function generateDrums(trackId: number, prompt: string, timelineStart: number, bars: number, bpm?: number, timeSignature?: string, referenceClipId?: number, useLibraryReference: boolean = true) {
    if (!userStore.user || !currentMix.value) return
    generating.value = true
    generateError.value = ''
    try {
      const token = await userStore.user.getIdToken()
      const body: any = { prompt, bars, bpm: bpm || 120, timeSignature: timeSignature || '4/4', useLibraryReference }
      if (referenceClipId) body.referenceClipId = referenceClipId
      const bandId = useSongStore().currentSong?.bandId
      if (bandId) body.bandId = bandId

      const { data } = await api.post('/songs/generate-drums', body, {
        headers: { Authorization: `Bearer ${token}` }
      })

      const result = await pollJob(data.jobId, token)

      // Download audio blob
      const audioResponse = await api.get(`/songs/jobs/${data.jobId}/audio`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      })
      const blob = audioResponse.data as Blob
      const file = new File([blob], 'drums.wav', { type: 'audio/wav' })
      await addClip(trackId, file, timelineStart, undefined, { prompt, geminiResponse: result.midiJson || '', instrument: 'drums', inputTokens: result.inputTokens, outputTokens: result.outputTokens, aiModel: result.aiModel, aiThinking: result.aiThinking })
    } catch (error: any) {
      const promptLabel = error.promptText ? `Prompt "${error.promptText}" failed: ` : ''
      generateError.value = promptLabel + (error.message || 'Failed to generate drums')
      uiStore.showToast('error', 'AI Drums', generateError.value)
      throw error
    } finally {
      generating.value = false
    }
  }

  async function generatePiano(trackId: number, prompt: string, timelineStart: number, bars: number, bpm?: number, timeSignature?: string, referenceClipId?: number | null) {
    if (!userStore.user || !currentMix.value) return
    generating.value = true
    generateError.value = ''
    try {
      const token = await userStore.user.getIdToken()
      const body: any = { prompt, bars, bpm: bpm || 120, timeSignature: timeSignature || '4/4' }
      if (referenceClipId) {
        body.referenceClipId = referenceClipId
      }
      const pianoBandId = useSongStore().currentSong?.bandId
      if (pianoBandId) body.bandId = pianoBandId

      const { data } = await api.post('/songs/generate-piano', body, {
        headers: { Authorization: `Bearer ${token}` }
      })

      const result = await pollJob(data.jobId, token)

      // Download audio blob
      const audioResponse = await api.get(`/songs/jobs/${data.jobId}/audio`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      })
      const blob = audioResponse.data as Blob
      const file = new File([blob], 'piano.wav', { type: 'audio/wav' })
      await addClip(trackId, file, timelineStart, undefined, { prompt, geminiResponse: result.midiJson || '', instrument: 'piano', inputTokens: result.inputTokens, outputTokens: result.outputTokens, aiModel: result.aiModel, aiThinking: result.aiThinking })
    } catch (error: any) {
      const promptLabel = error.promptText ? `Prompt "${error.promptText}" failed: ` : ''
      generateError.value = promptLabel + (error.message || 'Failed to generate piano')
      uiStore.showToast('error', 'AI Piano', generateError.value)
      throw error
    } finally {
      generating.value = false
    }
  }

  async function generateBass(trackId: number, prompt: string, timelineStart: number, bars: number, bpm?: number, timeSignature?: string, referenceClipId?: number | null) {
    if (!userStore.user || !currentMix.value) return
    generating.value = true
    generateError.value = ''
    try {
      const token = await userStore.user.getIdToken()
      const body: any = { prompt, bars, bpm: bpm || 120, timeSignature: timeSignature || '4/4' }
      if (referenceClipId) {
        body.referenceClipId = referenceClipId
      }
      const bassBandId = useSongStore().currentSong?.bandId
      if (bassBandId) body.bandId = bassBandId

      const { data } = await api.post('/songs/generate-bass', body, {
        headers: { Authorization: `Bearer ${token}` }
      })

      const result = await pollJob(data.jobId, token)

      const audioResponse = await api.get(`/songs/jobs/${data.jobId}/audio`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      })
      const blob = audioResponse.data as Blob
      const file = new File([blob], 'bass.wav', { type: 'audio/wav' })
      await addClip(trackId, file, timelineStart, undefined, { prompt, geminiResponse: result.midiJson || '', instrument: 'bass', inputTokens: result.inputTokens, outputTokens: result.outputTokens, aiModel: result.aiModel, aiThinking: result.aiThinking })
    } catch (error: any) {
      const promptLabel = error.promptText ? `Prompt "${error.promptText}" failed: ` : ''
      generateError.value = promptLabel + (error.message || 'Failed to generate bass')
      uiStore.showToast('error', 'AI Bass', generateError.value)
      throw error
    } finally {
      generating.value = false
    }
  }

  async function classifyMidi(payload: {
    filename: string
    bpm: number
    timeSignature: string
    bars: number
    noteCount: number
    gridText: string
    useProfile?: boolean
    useLocal?: boolean
  }) {
    if (!userStore.user) throw new Error('Not authenticated')
    const token = await userStore.user.getIdToken()
    const response = await api.post('/songs/classify-midi', payload, {
      headers: { Authorization: `Bearer ${token}` }
    })
    return response.data as {
      responseText: string
      inputTokens: number
      outputTokens: number
      aiModel: string
      cost: number | null
    }
  }

  async function previewDrumPrompt(payload: {
    prompt: string
    bars: number
    bpm: number
    timeSignature: string
    useLibraryReference?: boolean
    format?: 'json' | 'grid'
  }) {
    if (!userStore.user) throw new Error('Not authenticated')
    const token = await userStore.user.getIdToken()
    const response = await api.post('/songs/preview-drum-prompt', payload, {
      headers: { Authorization: `Bearer ${token}` }
    })
    return response.data as {
      promptText: string
      resolvedFolderPrefix?: string | null
      retrievedLoops: Array<{
        id: number
        filename: string
        relativePath: string
        bpm: number
        timeSignature: string
        bars: number
        genre: string
        sectionHint: string
        feel: string
        density: string
        backbeatType: string
        gridText: string
        score: number
        matchReason: string
      }>
    }
  }

  async function renderDrumsCompare(payload: {
    prompt: string
    bars: number
    bpm: number
    timeSignature: string
    useLibraryReference?: boolean
  }) {
    if (!userStore.user) throw new Error('Not authenticated')
    const token = await userStore.user.getIdToken()
    const response = await api.post('/songs/dev/render-drums-compare', payload, {
      headers: { Authorization: `Bearer ${token}` }
    })
    return response.data as RenderDrumsCompareResultDto
  }

  async function renderMidiJson(trackId: number, timelineStart: number, payload: {
    midiJson: string
    instrument: string
    bpm: number
    duration: number
  }, filename: string) {
    if (!userStore.user || !currentMix.value) throw new Error('Not authenticated or no mix')
    const token = await userStore.user.getIdToken()
    const response = await api.post('/songs/render-midi-json', payload, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob'
    })
    const blob = response.data as Blob
    const file = new File([blob], filename, { type: 'audio/wav' })
    return await addClip(trackId, file, timelineStart, payload.duration)
  }

  async function renderStepGrid(trackId: number, timelineStart: number, payload: {
    gridText: string
    bars: number
    bpm: number
    timeSignature: string
    instrument: string
    duration?: number
  }, filename: string) {
    if (!userStore.user || !currentMix.value) throw new Error('Not authenticated or no mix')
    const token = await userStore.user.getIdToken()
    const response = await api.post('/songs/render-stepgrid', payload, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob'
    })
    const blob = response.data as Blob
    const file = new File([blob], filename, { type: 'audio/wav' })
    const beatsPerBar = parseInt(payload.timeSignature.split('/')[0] || '4', 10) || 4
    const dur = payload.duration ?? Math.max(4, Math.ceil(payload.bars * beatsPerBar * 60 / payload.bpm + 2))
    return await addClip(trackId, file, timelineStart, dur)
  }

  async function renderMidi(trackId: number, timelineStart: number, payload: {
    bpm: number
    duration: number
    instrument: string
    notes: { pitch: number, time: number, duration: number, velocity: number }[]
  }, filename: string) {
    if (!userStore.user || !currentMix.value) throw new Error('Not authenticated or no mix')
    const token = await userStore.user.getIdToken()
    const response = await api.post('/songs/render-midi', payload, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob'
    })
    const blob = response.data as Blob
    const file = new File([blob], filename.replace(/\.midi?$/i, '.wav'), { type: 'audio/wav' })
    return await addClip(trackId, file, timelineStart, payload.duration)
  }

  async function duplicateClip(clipId: number, timelineStart: number) {
    if (!userStore.user || !currentMix.value) return
    try {
      const token = await userStore.user.getIdToken()
      const response = await api.post(`/mixes/clips/${clipId}/duplicate`, { timelineStart }, {
        headers: { Authorization: `Bearer ${token}` }
      })

      // Find the track this clip belongs to and add the duplicate
      for (const track of currentMix.value.tracks) {
        if (track.clips.some(c => c.id === clipId)) {
          track.clips.push(response.data)
          await audioEngine.loadClip(response.data, STATIC_BASE_URL)
          break
        }
      }
      return response.data
    } catch (error) {
      console.error('Failed to duplicate clip:', error)
      throw error
    }
  }

  async function bounceClip(clipId: number) {
    if (!userStore.user || !currentMix.value) return
    try {
      const token = await userStore.user.getIdToken()
      const { data } = await api.post(`/mixes/clips/${clipId}/bounce`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      })
      // Update the clip in place
      for (const track of currentMix.value.tracks) {
        const clip = track.clips.find(c => c.id === clipId)
        if (clip) {
          Object.assign(clip, data)
          break
        }
      }
      return data
    } catch (error) {
      console.error('Failed to bounce clip:', error)
      throw error
    }
  }

  async function adjustAiClip(clipId: number, prompt: string) {
    if (!userStore.user || !currentMix.value) return
    generating.value = true
    generateError.value = ''
    try {
      const token = await userStore.user.getIdToken()
      const { data } = await api.post(`/mixes/clips/${clipId}/adjust`, { prompt }, {
        headers: { Authorization: `Bearer ${token}` }
      })

      // Add pending prompt to local clip immediately so it shows in history
      for (const track of currentMix.value.tracks) {
        const clip = track.clips.find(c => c.id === clipId)
        if (clip) {
          const maxSeq = clip.prompts.length ? Math.max(...clip.prompts.map(p => p.sequence)) : 0
          clip.prompts.push({
            id: -1, // temporary
            sequence: maxSeq + 1,
            prompt,
            status: 'Pending',
            createdAt: new Date().toISOString(),
            userId: 0,
            audioClipId: clipId,
            model: '',
            thinking: true
          })
          break
        }
      }

      const result = await pollJob(data.jobId, token)

      // Update clip locally with full server response (includes updated prompt with Completed status)
      if (result.clip) {
        for (const track of currentMix.value.tracks) {
          const idx = track.clips.findIndex(c => c.id === clipId)
          if (idx !== -1) {
            track.clips[idx] = result.clip
            await audioEngine.loadClip(result.clip, STATIC_BASE_URL)
            break
          }
        }
      }
      return result.clip
    } catch (error: any) {
      // Update the pending prompt to failed status locally
      if (currentMix.value) {
        for (const track of currentMix.value.tracks) {
          const clip = track.clips.find(c => c.id === clipId)
          if (clip) {
            const pending = clip.prompts.find(p => p.status === 'Pending')
            if (pending) pending.status = 'Failed'
            break
          }
        }
      }
      const promptLabel = error.promptText ? `Prompt "${error.promptText}" failed: ` : ''
      generateError.value = promptLabel + (error.message || 'Adjustment failed')
      uiStore.showToast('error', 'AI Adjustment', generateError.value)
      throw error
    } finally {
      generating.value = false
    }
  }

  async function revertClipPrompt(clipId: number, promptId: number) {
    if (!userStore.user || !currentMix.value) return
    generating.value = true
    generateError.value = ''
    try {
      const token = await userStore.user.getIdToken()
      const { data } = await api.post(`/mixes/clips/${clipId}/revert`, { promptId }, {
        headers: { Authorization: `Bearer ${token}` }
      })

      const result = await pollJob(data.jobId, token)

      if (result.clip) {
        for (const track of currentMix.value.tracks) {
          const idx = track.clips.findIndex(c => c.id === clipId)
          if (idx !== -1) {
            track.clips[idx] = result.clip
            await audioEngine.loadClip(result.clip, STATIC_BASE_URL)
            break
          }
        }
      }
      return result.clip
    } catch (error: any) {
      generateError.value = error.message || 'Revert failed'
      uiStore.showToast('error', 'AI Revert', generateError.value)
      throw error
    } finally {
      generating.value = false
    }
  }

  function setMetronomeSettings(bpm: number, beatsPerBar: number) {
    metronomeBpm.value = bpm
    metronomeBeatsPerBar.value = beatsPerBar
    if (metronomeEnabled.value && isPlaying.value) {
      audioEngine.startMetronome(bpm, beatsPerBar, currentTime.value)
    }
  }

  function toggleMetronome() {
    metronomeEnabled.value = !metronomeEnabled.value
    if (isPlaying.value) {
      if (metronomeEnabled.value) {
        audioEngine.startMetronome(metronomeBpm.value, metronomeBeatsPerBar.value, currentTime.value)
      } else {
        audioEngine.stopMetronome()
      }
    }
  }

  function zoomIn() {
    if (zoomIndex.value < ZOOM_STEPS.length - 1) {
      zoomIndex.value++
      pixelsPerSecond.value = ZOOM_STEPS[zoomIndex.value]!
    }
  }

  function zoomOut() {
    if (zoomIndex.value > 0) {
      zoomIndex.value--
      pixelsPerSecond.value = ZOOM_STEPS[zoomIndex.value]!
    }
  }

  return {
    currentMix, loading, uploading, generating, generateError, isPlaying, isRecording, armedTrackId, currentTime, masterLevel, recordStartTime,
    metronomeEnabled, metronomeBpm, metronomeBeatsPerBar, dragState,
    fetchMixDetails, addTrack, updateTrack, deleteTrack,
    addClip, deleteClip, updateClip, moveClipToTrack, splitClip, duplicateClip, bounceClip, updateMix,
    play, stop, stopRecording, toggleRecord, armTrack, seek, clear,
    generateDrums, generatePiano, generateBass, classifyMidi, renderMidi, renderMidiJson, renderStepGrid, previewDrumPrompt, renderDrumsCompare, adjustAiClip, revertClipPrompt,
    setMetronomeSettings, toggleMetronome,
    zoomIn, zoomOut
  }
})
